import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "../../../theme/customTheme";

export const useStyles = makeStyles({
  calendarPortal: {
    position: "relative",
    zIndex: 1000000,
    // Selected day
    "& [aria-selected='true']": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
      ":hover": {
        backgroundColor: `${designTokens.accent[700]} !important`,
        color: `${designTokens.neutral.white} !important`,
      },
    },
    // Today highlight
    "& .fui-Calendar__dayIsToday": {
      backgroundColor: `${designTokens.accent[100]} !important`,
      color: `${designTokens.neutral[900]} !important`,
      fontWeight: designTokens.fontWeight.semibold,
    },
    // Hover on day/month/year buttons
    "& button:hover:not([aria-selected='true']):not([disabled])": {
      backgroundColor: `${designTokens.accent[50]} !important`,
      color: `${designTokens.neutral[900]} !important`,
    },
    // Selected month/year in picker
    "& .fui-CalendarPicker__selected": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
      ":hover": {
        backgroundColor: `${designTokens.accent[700]} !important`,
        color: `${designTokens.neutral.white} !important`,
      },
    },
    // Current month/year highlight
    "& .fui-CalendarPicker__current": {
      backgroundColor: `${designTokens.accent[100]} !important`,
      color: `${designTokens.neutral[900]} !important`,
      fontWeight: designTokens.fontWeight.semibold,
    },
    // Selected day cell
    "& .fui-CalendarDayGrid__daySelected": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
    },
    "& .fui-CalendarDayGrid__daySingleSelected": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
    },
    // Pressed state
    "& .fui-CalendarDayGrid__pressedStyle": {
      backgroundColor: `${designTokens.accent[700]} !important`,
      color: `${designTokens.neutral.white} !important`,
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
    // width: "100%",
    width: "400px",
    boxSizing: "border-box",
  },
  datePickerWrapper: {
    position: "relative",
    width: "100%",
    "& .fui-Calendar__dayButton:hover:not([aria-selected='true'])": {
      backgroundColor: `${designTokens.accent[50]} !important`,
      color: `${designTokens.neutral[900]} !important`,
    },
    "& .fui-Calendar__dayCell:hover .fui-Calendar__dayButton": {
      backgroundColor: `${designTokens.accent[50]} !important`,
      color: `${designTokens.neutral[900]} !important`,
    },
    "& [role='gridcell'] button:hover": {
      backgroundColor: `${designTokens.accent[50]} !important`,
      color: `${designTokens.neutral[900]} !important`,
    },
    "& .fui-Calendar__dayIsToday": {
      backgroundColor: `${designTokens.accent[100]} !important`,
      color: `${designTokens.neutral[900]} !important`,
      fontWeight: designTokens.fontWeight.semibold,
    },
    "& .fui-Calendar__dayIsSelected": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
    },
    "& [aria-selected='true']": {
      backgroundColor: `${designTokens.accent[600]} !important`,
      color: `${designTokens.neutral.white} !important`,
      ":hover": {
        backgroundColor: `${designTokens.accent[700]} !important`,
        color: `${designTokens.neutral.white} !important`,
      },
    },
  },
  datePickerField: {
    width: "100%",
    "&.fui-Input": {
      height: "36px",
      minHeight: "36px",
      boxSizing: "border-box",
      backgroundColor: designTokens.neutral.white,
      ...shorthands.border("1px", "solid", designTokens.neutral[300]),
      ...shorthands.borderRadius(designTokens.borderRadius.md),
      transition: `border-color ${designTokens.transition.fast}`,
      ":hover": {
        ...shorthands.borderColor(designTokens.neutral[400]),
      },
      ":focus-within": {
        ...shorthands.borderColor(designTokens.accent[500]),
        boxShadow: `0 0 0 0px ${designTokens.accent[100]}`,
      },
    },
    "& .fui-Input__input": {
      height: "34px",
      minHeight: "34px",
      paddingTop: "0",
      paddingBottom: "0",
      paddingLeft: designTokens.spacing[3],
      paddingRight: designTokens.spacing[3],
      fontFamily: designTokens.fontFamily,
      fontSize: designTokens.fontSize.sm,
      color: designTokens.neutral[700],
      backgroundColor: "transparent",
      ...shorthands.outline("none"),
      "::placeholder": {
        color: designTokens.neutral[500],
      },
    },
    "& .fui-Input__contentAfter": {
      height: "34px",
      width: "34px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: designTokens.neutral[500],
      backgroundColor: "transparent",
      ...shorthands.border("0"),
      ...shorthands.borderRadius(designTokens.borderRadius.md),
      marginRight: designTokens.spacing[1],
      paddingLeft: "0",
      paddingRight: "0",
      ":hover": {
        backgroundColor: designTokens.neutral[50],
        color: designTokens.neutral[700],
      },
    },
    "& .fui-Input__contentAfter svg": {
      fontSize: designTokens.iconSize.sm,
    },
    "& button": {
      color: designTokens.neutral[500],
    },
  },
  datePicker: {
    width: "100%",
    height: "36px",
    minWidth: "140px",
    boxSizing: "border-box",
    backgroundColor: designTokens.neutral.white,
    ...shorthands.border("1px", "solid", designTokens.neutral[300]),
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    paddingTop: "0",
    paddingBottom: "0",
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    ...shorthands.outline("none"),
    cursor: "pointer",
    transition: `border-color ${designTokens.transition.fast}`,
    appearance: "none",
    ":focus": {
      ...shorthands.borderColor(designTokens.accent[500]),
      boxShadow: `0 0 0 0px ${designTokens.accent[100]}`,
    },
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
    width: "100%",
  },
  label: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    marginBottom: designTokens.spacing[1],
    marginRight: designTokens.spacing[2],
  },
  customDateContainer: {
    display: "flex",
    flexDirection: "row",
    gap: designTokens.spacing[4],
    flexWrap: "wrap",
    width: "100%",
    "@media (max-width: 640px)": {
      flexDirection: "column",
    },
  },
  // datePickerWrapper: {
  //   display: "flex",
  //   flexDirection: "column",
  //   gap: designTokens.spacing[2],
  //   flex: "1 1 200px",
  //   minWidth: "0",
  // },
  errorText: {
    color: designTokens.semantic.error,
    fontSize: designTokens.fontSize.xs,
    marginTop: designTokens.spacing[1],
  },
  selectedPeriodText: {
    marginTop: designTokens.spacing[3],
    ...shorthands.padding(designTokens.spacing[3]),
    backgroundColor: designTokens.neutral[50],
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
    fontWeight: designTokens.fontWeight.medium,
    wordBreak: "break-word",
  },
  selectWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    width: "100%",
    position: "relative",
  },
  selectField: {
    width: "100%",
    "& select": {
      height: "36px",
      minWidth: "140px",
      boxSizing: "border-box",
      backgroundColor: designTokens.neutral.white,
      ...shorthands.border("1px", "solid", designTokens.neutral[300]),
      ...shorthands.borderRadius(designTokens.borderRadius.md),
      paddingTop: "0",
      paddingBottom: "0",
      paddingLeft: designTokens.spacing[3],
      paddingRight: designTokens.spacing[8],
      fontFamily: designTokens.fontFamily,
      fontSize: designTokens.fontSize.sm,
      color: designTokens.neutral[700],
      ...shorthands.outline("none"),
      cursor: "pointer",
      transition: `border-color ${designTokens.transition.fast}`,
      appearance: "none",
      ":focus": {
        ...shorthands.borderColor(designTokens.accent[500]),
        boxShadow: `0 0 0 0px ${designTokens.accent[100]}`,
      },
    },
  },
  selectIcon: {
    position: "absolute",
    right: designTokens.spacing[2],
    bottom: "10px",
    width: designTokens.iconSize.sm,
    height: designTokens.iconSize.sm,
    color: designTokens.neutral[500],
    pointerEvents: "none",
  },
  applyButton: {
    marginTop: designTokens.spacing[2],
    alignSelf: "flex-start",
    minWidth: "140px",
  },
  divider: {
    height: "1px",
    backgroundColor: designTokens.neutral[200],
    ...shorthands.margin(designTokens.spacing[2], "0"),
    width: "100%",
  },
});

export const datePickerStyles = {
  root: {
    width: "100%",
    minWidth: "0",
  },
};

export const selectStyles = {
  root: {
    width: "100%",
    minWidth: "0",
  },
};
